/**
 * Statblocks cache hook - uses the new /api/statblocks endpoint
 * This replaces the Zifusion data cache for direct statblock fetching
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { generateCsvCacheKey } from '../utils/cacheKeys.js';
import { getCacheData, setCacheData } from '../utils/cacheStorage.js';
import { fetchAndTransformStatblocks } from '../services/statblocksService.js';

// Stable empty objects to prevent unnecessary re-renders
const EMPTY_STATBLOCKS = {};
const EMPTY_CSV_SECTIONS = [];
const EMPTY_WEATHER_DATA = null;

/**
 * Statblocks cache hook
 * @param {string|null} startDateStr - Start date string in YYYY-MM-DD format
 * @param {string|null} endDateStr - End date string in YYYY-MM-DD format
 * @param {string|null} storeId - Store ID or null for no-store
 * @param {Object|null} storeData - Full store object with name for display
 * @param {Array<string>} selectedStatblocks - Array of statblock types to include
 * @returns {Object} Hook state and methods
 */
export function useStatblocksCache(startDateStr, endDateStr, storeId, storeData = null, selectedStatblocks = null) {
  const [statblocks, setStatblocks] = useState(EMPTY_STATBLOCKS);
  const [csvSections, setCsvSections] = useState(EMPTY_CSV_SECTIONS);
  const [weatherData, setWeatherData] = useState(EMPTY_WEATHER_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');
  
  // Track current request to prevent race conditions
  const currentRequestRef = useRef(null);
  const abortControllerRef = useRef(null);

  /**
   * Fetch statblocks from the new API endpoint
   */
  const fetchFromStatblocksAPI = useCallback(async (startDate, endDate, store, storeName = 'Store') => {
    try {
      setCacheStatus('network-loading');
      
      console.log(`Fetching statblocks for store ${store} from ${startDate} to ${endDate}`);
      
      // Fetch statblocks directly from the new API
      const result = await fetchAndTransformStatblocks(
        store, 
        startDate, 
        endDate, // Use actual date range
        selectedStatblocks
      );
      
      if (!result) {
        throw new Error('No data returned from statblocks API');
      }
      
      console.log(`Successfully fetched statblocks for store ${store}:`, {
        statblockTypes: Object.keys(result.statblocks),
        recordsProcessed: result.meta?.records_processed || 0,
        dataSource: result.meta?.data_source
      });
      
      return result;
      
    } catch (error) {
      console.error('Error fetching from statblocks API:', error);
      throw error;
    }
  }, [selectedStatblocks]);

  /**
   * Main data fetching function
   */
  const fetchData = useCallback(async (startDate, endDate, store) => {
    if (!startDate || !endDate) {
      setStatblocks(EMPTY_STATBLOCKS);
      setCsvSections(EMPTY_CSV_SECTIONS);
      setWeatherData(EMPTY_WEATHER_DATA);
      setError(null);
      setCacheStatus('idle');
      return;
    }

    if (!store || store === 'no-store') {
      // No store selected - clear data
      setStatblocks(EMPTY_STATBLOCKS);
      setCsvSections(EMPTY_CSV_SECTIONS);
      setWeatherData(EMPTY_WEATHER_DATA);
      setError(null);
      setCacheStatus('idle');
      return;
    }

    // Generate cache key (include both dates for range support)
    const cacheKey = generateCsvCacheKey(`${startDate}_${endDate}`, store);
    const requestId = `${startDate}-${endDate}-${store}-${Date.now()}`;
    currentRequestRef.current = requestId;

    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cachedData = getCacheData(cacheKey);
      if (cachedData && cachedData.statblocks) {
        // Verify this is still the current request
        if (currentRequestRef.current !== requestId) return;
        
        setStatblocks(cachedData.statblocks || {});
        setCsvSections(cachedData.csvSections || []);
        setWeatherData(cachedData.weatherData || null);
        setCacheStatus('cache-hit');
        setLoading(false);
        return;
      }

      // Cache miss - fetch from statblocks API
      const storeName = storeData?.name || `Store ${store}`;
      const result = await fetchFromStatblocksAPI(startDate, endDate, store, storeName);

      // Verify this is still the current request
      if (currentRequestRef.current !== requestId || !result) return;

      // Update state
      setStatblocks(result.statblocks || {});
      setCsvSections(result.csvSections || []);
      setWeatherData(result.weatherData || null);
      setCacheStatus('cache-miss');

      // Cache the result
      setCacheData(cacheKey, result);

    } catch (err) {
      // Verify this is still the current request
      if (currentRequestRef.current !== requestId) return;
      
      console.error('Error fetching statblocks:', err);
      setError(err.message || 'Failed to fetch statblocks from API');
      setCacheStatus('error');
      setStatblocks(EMPTY_STATBLOCKS);
      setCsvSections(EMPTY_CSV_SECTIONS);
      setWeatherData(EMPTY_WEATHER_DATA);
    } finally {
      // Verify this is still the current request
      if (currentRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [fetchFromStatblocksAPI, storeData]);

  /**
   * Refresh cache - force network fetch
   */
  const refreshCache = useCallback(async () => {
    if (!startDateStr || !endDateStr) return;
    
    // Clear cache for this key
    const cacheKey = generateCsvCacheKey(`${startDateStr}_${endDateStr}`, storeId);
    setCacheData(cacheKey, null);
    
    // Fetch fresh data
    await fetchData(startDateStr, endDateStr, storeId);
  }, [startDateStr, endDateStr, storeId, fetchData]);

  /**
   * Update statblocks manually (for external updates)
   */
  const updateStatblocks = useCallback((newStatblocks) => {
    setStatblocks(newStatblocks);
    
    // Update cache if we have a valid cache key
    if (startDateStr && endDateStr) {
      const cacheKey = generateCsvCacheKey(`${startDateStr}_${endDateStr}`, storeId);
      const cachedData = getCacheData(cacheKey) || {};
      setCacheData(cacheKey, {
        ...cachedData,
        statblocks: newStatblocks
      });
    }
  }, [startDateStr, endDateStr, storeId]);

  /**
   * Update CSV sections manually (for compatibility)
   */
  const updateCsvSections = useCallback((newSections) => {
    setCsvSections(newSections);
    
    // Update cache if we have a valid cache key
    if (startDateStr && endDateStr) {
      const cacheKey = generateCsvCacheKey(`${startDateStr}_${endDateStr}`, storeId);
      const cachedData = getCacheData(cacheKey) || {};
      setCacheData(cacheKey, {
        ...cachedData,
        csvSections: newSections
      });
    }
  }, [startDateStr, endDateStr, storeId]);

  // Effect to fetch data when dependencies change
  useEffect(() => {
    fetchData(startDateStr, endDateStr, storeId);
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [startDateStr, endDateStr, storeId, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Direct statblocks (no need to compute them)
    statblocks,
    
    // CSV sections (empty for compatibility)
    csvSections,
    
    // Weather data (null for Zifusion)
    weatherData,
    
    // Loading and error states
    loading,
    error,
    cacheStatus,
    
    // Cache status helpers
    isCacheHit: cacheStatus === 'cache-hit',
    isNetworkLoading: cacheStatus === 'network-loading',
    hasError: !!error,
    
    // Utility functions
    refreshCache,
    updateStatblocks,
    updateCsvSections
  };
}
